import { createAction, PayloadAction } from '@reduxjs/toolkit';
import { all, call, put, select, takeLatest, takeLeading } from 'redux-saga/effects';

import CovidDSApi, { CovidDataLink, VaccineRiskCategories } from 'api/CovidDSApi';
import LocationApi from 'api/LocationApi';
import LogApi from 'api/LogApi';
import { showNotification } from 'app-redux/notification.saga';
import { PromiseArrayUnwrap } from 'utils/typeHelpers';

import { LocationInfo } from 'components/_controls/AoiSearch/models';

import * as covidDSLS from './covidDataSuite.localStorage';
import * as CovidDSSelectors from './covidDataSuite.selectors';
import {
  dataLastUpdateDateError,
  dataLastUpdateDateLoaded,
  downloadCovidDataEnd,
  downloadCovidDataError,
  downloadCovidDataStart,
  loadLocationCovidDataError,
  loadLocationCovidDataStart,
  loadLocationCovidDataSuccess,
  loadVaccineDataError,
  loadVaccineDataStart,
  loadVaccineDataSuccess,
  removeVaccineTrackerLocation,
} from './covidDataSuite.slice';

export type LocationsUpdateEvent = {
  added?: LocationInfo[];
  deleted?: number[];
};
export type VaccineLocationsLoadRequest = {
  startDate: Date;
  endDate: Date;
  targetHighRiskRate: number;
  targetTotalRate: number;
};
export type VaccineLocationsRequest = {
  locations: LocationsUpdateEvent;
  startDate: Date;
  endDate: Date;
  targetHighRiskRate: number;
  targetTotalRate: number;
};

export const fetchCovidData = createAction<number>('covidDataSuite/fetchData');
export const fetchDataLastUpdated = createAction<void>('covidDataSuite/fetchDataLastUpdated');
export const downloadCovidData = createAction<void>('covidDataSuite/downloadCovidData');
export const updateVaccineTrackerLocations = createAction<VaccineLocationsRequest>(
  'covidDataSuite/updateVaccineTrackerLocations'
);
export const loadVaccineTrackerLocationsFromLocalStorage =
  createAction<VaccineLocationsLoadRequest>(
    'covidDataSuite/loadVaccineTrackerLocationsFromLocalStorage'
  );

export function* fetchCovidDataSaga(action: PayloadAction<number>) {
  try {
    yield put(loadLocationCovidDataStart());
    const prevAddLocations = yield select(CovidDSSelectors.getNumOfLocations);
    if (prevAddLocations >= 8) {
      yield put(
        loadLocationCovidDataError(
          'The maximum number of locations to add is 8, please remove one location to add another'
        )
      );
      return;
    }

    const res = yield call(CovidDSApi.getCovidDataByGeonameId, { locationId: action.payload });

    yield put(
      showNotification({
        type: 'SHOW_SUCCESS_NOTIFICATION',
        payload: 'My locations successfully saved',
      })
    );
    yield put(loadLocationCovidDataSuccess(res));
  } catch (e) {
    LogApi.sendLog('error', `Message: ${e?.message}, Stack: ${e?.stack}`);
    console.error(e);
    yield put(loadLocationCovidDataError('Unexpected internal server error. Please try again'));
  }
}

export function* fetchDataLastUpdatedSaga() {
  try {
    const date = yield call(CovidDSApi.getCovidDataLastUpdated);

    yield put(dataLastUpdateDateLoaded(date));
  } catch (e) {
    LogApi.sendLog('error', `Message: ${e?.message}, Stack: ${e?.stack}`);
    console.error(e);
    yield put(dataLastUpdateDateError());
  }
}

export function* downloadCovidDataSaga() {
  try {
    yield put(downloadCovidDataStart());

    const res: CovidDataLink = yield call(CovidDSApi.getCovidDataDownloadLink);

    window.location.assign(res.url);

    yield put(downloadCovidDataEnd());
  } catch (e) {
    LogApi.sendLog('error', `Message: ${e?.message}, Stack: ${e?.stack}`);
    console.error(e);
    yield put(downloadCovidDataError());
  }
}

export function* updateVaccineTrackerLocationsSaga(action: PayloadAction<VaccineLocationsRequest>) {
  const { locations, startDate, endDate, targetHighRiskRate, targetTotalRate } = action.payload;
  const newItems = locations?.added || [];
  const deletedItems = locations?.deleted || [];

  for (const deleted of deletedItems) {
    yield put(removeVaccineTrackerLocation(deleted));
  }

  try {
    if (newItems?.length) {
      yield put(loadVaccineDataStart(newItems.length));

      const res: VaccineRiskCategories[] = yield call(CovidDSApi.getVaccinationInfo, {
        locationIds: newItems.map(d => d.id),
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        targetHighRiskRate,
        targetTotalRate,
      });

      yield put(loadVaccineDataSuccess({ vaccineResults: res, locationsInfo: newItems }));
    }
    yield put(
      showNotification({
        type: 'SHOW_SUCCESS_NOTIFICATION',
        payload:
          newItems.length === 1 && !deletedItems.length
            ? 'Location successfully added.'
            : 'Locations successfully updated.',
      })
    );
  } catch (e) {
    LogApi.sendLog('error', `Message: ${e?.message}, Stack: ${e?.stack}`);
    console.error(e);
    yield put(loadVaccineDataError());
  }
}

export function* loadVaccineTrackerLocationsFromLocalStorageSaga(
  action: PayloadAction<VaccineLocationsLoadRequest>
) {
  const { startDate, endDate, targetHighRiskRate, targetTotalRate } = action.payload;

  const storedLocationIds = covidDSLS.getVtLocationIds();

  try {
    yield put(loadVaccineDataStart(storedLocationIds.length));

    const apiCalls = [
      LocationApi.getGeoname({ geonameIds: storedLocationIds, includeShape: false }),
      CovidDSApi.getVaccinationInfo({
        locationIds: storedLocationIds,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        targetHighRiskRate,
        targetTotalRate,
      }),
    ] as const;

    const [resInfo, resVac]: PromiseArrayUnwrap<typeof apiCalls> = yield all(apiCalls.map(x => x));

    yield put(
      loadVaccineDataSuccess({
        vaccineResults: resVac,
        locationsInfo: resInfo.map(
          x =>
            ({
              id: x.geonameId,
              name: x.name,
              displayName: x.fullDisplayName,
              provinceName: x.province,
              countryName: x.country,
              locationType: x.locationType,
            } as LocationInfo)
        ),
      })
    );
  } catch (e) {
    LogApi.sendLog('error', `Message: ${e?.message}, Stack: ${e?.stack}`);
    console.error(e);
    yield put(loadVaccineDataError());
  }
}

function* covidDataSuiteSaga() {
  yield takeLatest(fetchCovidData, fetchCovidDataSaga);
  yield takeLatest(fetchDataLastUpdated, fetchDataLastUpdatedSaga);
  yield takeLatest(updateVaccineTrackerLocations, updateVaccineTrackerLocationsSaga);
  yield takeLatest(
    loadVaccineTrackerLocationsFromLocalStorage,
    loadVaccineTrackerLocationsFromLocalStorageSaga
  );
  yield takeLeading(downloadCovidData, downloadCovidDataSaga);
}

export default covidDataSuiteSaga;
